import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import '../styles/filter-dropdown-scrollbar.css';

interface FilterOption {
  value: string;
  label: string;
  icon?: string;
}

interface UnifiedFilterDropdownProps {
  orderTypeFilters: string[];
  dateFilter: string;
  onOrderTypeChange: (types: string[]) => void;
  onDateFilterChange: (date: string) => void;
  className?: string;
}

const UnifiedFilterDropdown: React.FC<UnifiedFilterDropdownProps> = ({
  orderTypeFilters,
  dateFilter,
  onOrderTypeChange,
  onDateFilterChange,
  className = ''
}) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const orderTypeOptions: FilterOption[] = [
    { value: 'TABLE_SERVICE', label: 'Table Orders', icon: '🍽️' },
    { value: 'TAKEAWAY', label: 'Takeaway', icon: '🥡' },
    { value: 'DELIVERY', label: 'Delivery', icon: '🚗' },
    { value: 'COUNTER_SERVICE', label: 'Counter', icon: '🛎️' }
  ];

  const dateOptions: FilterOption[] = [
    { value: 'recent', label: 'Recent', icon: '🔄' },
    { value: 'today', label: 'Today', icon: '📅' },
    { value: 'yesterday', label: 'Yesterday', icon: '📆' },
    { value: 'week', label: 'This Week', icon: '📊' },
    { value: 'month', label: 'This Month', icon: '📈' },
    { value: 'all', label: 'All', icon: '📋' }
  ];

  const handleOrderTypeToggle = (type: string) => {
    if (orderTypeFilters.includes(type)) {
      onOrderTypeChange(orderTypeFilters.filter(t => t !== type));
    } else {
      onOrderTypeChange([...orderTypeFilters, type]);
    }
  };

  const handleDateChange = (date: string) => {
    // If clicking the same filter, deselect it and go back to 'recent'
    if (dateFilter === date) {
      onDateFilterChange('recent');
    } else {
      onDateFilterChange(date);
    }
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const activeFilterCount = orderTypeFilters.length + (dateFilter !== 'recent' && dateFilter !== 'all' ? 1 : 0);

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md border transition-colors whitespace-nowrap"
        style={{
          backgroundColor: theme.components.card.background,
          color: theme.text,
          borderColor: theme.components.card.border
        }}
      >
        <span>🔍</span>
        <span className="hidden xs:inline">Filters</span>
        {activeFilterCount > 0 && (
          <span
            className="px-1 py-0.5 text-[10px] rounded-full font-bold min-w-[16px] text-center"
            style={{
              backgroundColor: theme.primary,
              color: 'var(--text-inverse)'
            }}
          >
            {activeFilterCount}
          </span>
        )}
        <ChevronDownIcon className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div
            className="fixed sm:absolute inset-x-4 top-20 sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-1 sm:w-72 rounded-lg shadow-lg border z-20 overflow-hidden max-h-[60vh] overflow-y-auto filter-dropdown-scrollable"
            style={{
              backgroundColor: theme.components.card.background,
              borderColor: theme.components.card.border,
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.primary}50 transparent`
            }}
          >
            {/* Order Type Section */}
            <div className="p-3 border-b" style={{ borderColor: theme.components.card.border }}>
              <div className="text-xs font-semibold mb-2 opacity-75" style={{ color: theme.text }}>
                Order Type
              </div>
              <div className="space-y-1">
                {orderTypeOptions.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 p-2 rounded cursor-pointer transition-colors hover:bg-opacity-50"
                    style={{
                      backgroundColor: orderTypeFilters.includes(option.value)
                        ? theme.primary + '15'
                        : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!orderTypeFilters.includes(option.value)) {
                        e.currentTarget.style.backgroundColor = theme.gray[100] || '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!orderTypeFilters.includes(option.value)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={orderTypeFilters.includes(option.value)}
                      onChange={() => handleOrderTypeToggle(option.value)}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: theme.primary }}
                    />
                    <span className="text-lg">{option.icon}</span>
                    <span className="text-sm flex-1" style={{ color: theme.text }}>
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Date Range Section */}
            <div className="p-3">
              <div className="text-xs font-semibold mb-2 opacity-75" style={{ color: theme.text }}>
                Date Range
              </div>
              <div className="space-y-1">
                {dateOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleDateChange(option.value)}
                    className="w-full flex items-center gap-2 p-2 rounded text-left transition-colors"
                    style={{
                      backgroundColor: dateFilter === option.value ? theme.primary + '15' : 'transparent',
                      color: dateFilter === option.value ? theme.primary : theme.text
                    }}
                    onMouseEnter={(e) => {
                      if (dateFilter !== option.value) {
                        e.currentTarget.style.backgroundColor = theme.gray[100] || '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (dateFilter !== option.value) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span className="text-lg">{option.icon}</span>
                    <span className="text-sm flex-1">{option.label}</span>
                    {dateFilter === option.value && (
                      <span className="text-sm">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UnifiedFilterDropdown;
